import { ColorPaletteIcon } from '@ipfabric/ui-components/IntentVerification/ColorPaletteIcon';
import { getSeverityBgClass } from '@ipfabric/ui-components/IntentVerification/IntentVerification.styles';
import type { ReportSeverity } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { useApiTableAdvancedFilterColumns } from 'components/ApiTable/components/useApiTableAdvancedFilterColumns';
import { useApiTableContextSelectors } from 'components/ApiTable/model/ApiTableModel';
import { changeAdvancedFilter } from 'components/ApiTable/model/commands/changeAdvancedFilter';
import { submitAdvancedFilters } from 'components/ApiTable/model/commands/submitAdvancedFilters';
import { FormInputError } from 'components/FormInputError/FormInputError';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { AdvancedFilter } from 'components/Table/components/AdvancedFilter/AdvancedFilter';
import { formatGroupFilter } from 'components/Table/components/AdvancedFilter/formatFilter';
import {
  type GroupFilter,
  emptyGroupFilter,
  useAdvancedFilter,
} from 'components/Table/components/AdvancedFilter/useAdvancedFilter';
import { useRuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { filterHasEmptyOperator } from 'components/Table/components/AdvancedFilter/validateRule';
import { Toggle } from 'components/Toggle/Toggle';
import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import type { FC } from 'react';
import { nestedFormikName } from 'utils/formik/nestedFormikName';
import type { IntentVerificationRuleFormValues } from './IntentVerificationRuleForm';

interface Props {
  severity: ReportSeverity;
  formik: FormikProps<IntentVerificationRuleFormValues>;
}

export const IntentVerificationRuleCheck: FC<Props> = (props) => {
  const {
    apiTable: {
      intentVerification: { form: formMsg },
    },
  } = useLocale();
  const ctxProps = useApiTableContextSelectors({
    dispatch: prop('dispatch'),
  });
  const columns = useApiTableAdvancedFilterColumns();

  const { values: formValues, errors: formErrors } = props.formik;
  const ruleFilterHasErrors = !!formErrors.checks?.[props.severity];
  const ruleFilterExist =
    formValues.checks?.[props.severity].filters?.length > 0;
  const ruleFilterHasEmptyOperator = filterHasEmptyOperator(
    formValues.checks?.[props.severity],
  );
  const previewDisabled =
    ruleFilterHasErrors || !ruleFilterExist || ruleFilterHasEmptyOperator;

  const ruleContext = useRuleContext();

  const handleChange = (value: GroupFilter) => {
    props.formik.setFieldValue(`checks[${props.severity}]`, value);
  };

  const advancedFilterHook = useAdvancedFilter({
    value: props.formik.values.checks[props.severity],
    onChange: handleChange,
  });

  const handleDefaultColorChange = (value: boolean) => {
    props.formik.setFieldValue('defaultColor', value ? props.severity : null);
    if (value) {
      props.formik.setFieldValue(`checks[${props.severity}]`, {
        ...emptyGroupFilter,
      });
    }
  };

  const handlePreviewClick = () => {
    ctxProps.dispatch(changeAdvancedFilter, {
      filters: formatGroupFilter(
        props.formik.values.checks[props.severity],
        ruleContext,
      ),
    });
    ctxProps.dispatch(submitAdvancedFilters);
  };

  return (
    <div className='max-w-content-default-legacy my-4'>
      <div className='flex items-center'>
        <div className='mr-8 flex w-60'>
          <div className='form__label mr-4'>{formMsg.defaultColor}</div>
          <Toggle
            value={props.formik.values.defaultColor === props.severity}
            onChange={handleDefaultColorChange}
          />
        </div>
        <div className='w-full'>
          <FormikTextField
            formik={props.formik}
            name={nestedFormikName(`descriptions.checks.${props.severity}`)}
            type='text'
            label={formMsg.description}
          />
        </div>
      </div>
      {props.formik.values.defaultColor === props.severity ? (
        <div className='rounded border border-solid border-gray-600 bg-default px-4 h-28 flex justify-between items-center text-xs font-bold'>
          <div className='flex items-center'>
            <div
              className={classNames(
                'h-6 w-6 rounded-xs text-contrast mr-4 flex justify-center items-center shrink-0',
                getSeverityBgClass(props.severity),
              )}
            >
              D
            </div>
            <div className='font-bold text-gray-900'>
              {formMsg.defaultColorDescription()}
            </div>
          </div>
          <ColorPaletteIcon
            title={null}
            className='shrink-0'
            width={71}
            height={71}
          />
        </div>
      ) : (
        <>
          <AdvancedFilter
            advancedFilterHook={advancedFilterHook}
            columns={columns}
            onSubmit={() => {}}
            onFilterChange={() => {}}
            key={props.severity}
            submitCount={0}
            disableColorOperators
            onPreviewClick={handlePreviewClick}
            previewDisabled={previewDisabled}
          />
          {props.formik.touched.checks?.[props.severity] &&
            !!props.formik.errors.checks?.[props.severity] && (
              <FormInputError
                error={props.formik.errors.checks?.[props.severity] as string}
              />
            )}
        </>
      )}
    </div>
  );
};
